// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {
	aimedEmotesOff,
	encodeEmote,
	hasRoomEmote,
	isAimedEmote,
	SERVER_MUTE_ATTRIBUTE,
} from '@app/features/voice_scene/net/RoomSignals';
import type {RoomBodyEmote, RoomEmote} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import RoomAvatarSignals from '@app/features/voice_scene/state/RoomAvatarSignals';
import VoiceSceneCamera from '@app/features/voice_scene/state/VoiceSceneCamera';
import {ROOM_EMOTE_TOPIC} from '@fluxer/constants/src/RoomCharacters';
import {ROOM_EMOTES_ATTRIBUTE} from '@fluxer/constants/src/RoomEmotes';

const logger = new Logger('RoomSignalCommands');

// An emoji, a guild emoji or a pet floats over the character; it moves no body.
const floats = (emote: RoomEmote): boolean => emote.kind === 'emoji' || emote.kind === 'custom' || emote.kind === 'pet';

// Observable: false outside a voice room and while my emote limit holds.
export function canSendEmote(): boolean {
	return MediaEngine.room !== null && !RoomAvatarSignals.emoteSendLimited;
}

// Whether I may play this emote: the wave always, a body emote when my own micup.emotes attribute (written by the
// server) lists it, as everyone else's client checks.
export function ownsRoomEmote(kind: 'wave' | RoomBodyEmote): boolean {
	return hasRoomEmote({kind}, MediaEngine.room?.localParticipant.attributes[ROOM_EMOTES_ATTRIBUTE]);
}

// Sends an emote to everyone in my voice room over LiveKit data (reliable) and plays it on my own character, since
// LiveKit does not hand data back to its sender. false: not in a room, server muted, limited, a body emote I do not
// have, a point or slap in a room of more people than characters drawn, or a movement while I lie knocked out.
export function sendEmote(emote: RoomEmote): boolean {
	const room = MediaEngine.room;
	if (!room) return false;
	const me = room.localParticipant;
	if (me.attributes[SERVER_MUTE_ATTRIBUTE] === 'true') return false;
	if (RoomAvatarSignals.knockedOut && !floats(emote)) return false;
	if (isAimedEmote(emote) && aimedEmotesOff(room)) return false;
	if (!hasRoomEmote(emote, me.attributes[ROOM_EMOTES_ATTRIBUTE])) return false;
	// The answer to a slap is limited by the slap limits (useRoomSignals), not as an emote of mine.
	if (emote.kind !== 'slapped' && !RoomAvatarSignals.takeEmoteSend(performance.now())) return false;
	me.publishData(encodeEmote(emote), {reliable: true, topic: ROOM_EMOTE_TOPIC}).catch((error) => {
		logger.warn('Room emote was not sent', error);
	});
	RoomAvatarSignals.emit({identity: me.identity, emote});
	return true;
}

// B: my character points at what the free pointer is over, or where the camera looks (pointDirection). false: no
// character of mine on screen, or sendEmote's.
export function sendPoint(): boolean {
	const handle = VoiceSceneCamera.room?.handle;
	const look = handle?.canEmote?.('point') ? handle.pointDirection?.() : null;
	return look ? sendEmote({kind: 'point', yawDeg: look.yawDeg, pitchDeg: look.pitchDeg}) : false;
}

// T: my character slaps the nearest standing character in front of it and steps up to them while walking is on. They
// only see it land if their own client answers (useRoomSignals). false: nobody in front, or sendEmote's.
export function sendSlap(): boolean {
	const handle = VoiceSceneCamera.room?.handle;
	const target = handle?.canEmote?.('slap') ? handle.slapTarget?.() : null;
	if (!target || !sendEmote({kind: 'slap', target: target.userId})) return false;
	VoiceSceneCamera.walk?.stepTo(target.spot, target.facing);
	return true;
}

export type SlapRefusal = 'seated' | 'crowded' | 'limited' | 'no_target';

// Why T would not slap now, for its short hint, the first that applies: seated, a room past the aimed-movement limit,
// my emote limit, nobody in front. null outside a room, for a character without a slap clip (T has no slap there),
// while the server mutes me and while I lie knocked out (the walking hint says so already).
export function slapRefusal(standing: boolean): SlapRefusal | null {
	const room = MediaEngine.room;
	const handle = VoiceSceneCamera.room?.handle;
	if (!room || !handle?.canEmote?.('slap')) return null;
	if (room.localParticipant.attributes[SERVER_MUTE_ATTRIBUTE] === 'true' || RoomAvatarSignals.knockedOut) return null;
	if (!standing) return 'seated';
	if (aimedEmotesOff(room)) return 'crowded';
	if (RoomAvatarSignals.emoteSendLimited) return 'limited';
	return handle.slapTarget?.() ? null : 'no_target';
}
